import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '../../../services/api';
import { formatDate } from '../../../utils/Utils';
import ExpenseAmountCell from '../components/ExpenseAmountCell';

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 'approved' ? '#2e7d3215' :
    $status === 'rejected' ? '#d32f2f15' :
    $status === 'canceled' ? '#9e9e9e15' :
    '#ed6c0215'};
  color: ${({ $status }) =>
    $status === 'approved' ? '#2e7d32' :
    $status === 'rejected' ? '#d32f2f' :
    $status === 'canceled' ? '#9e9e9e' :
    '#ed6c02'};
`;

const PaymentBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) => $status === 'paid' ? '#2e7d3215' : '#ed6c0215'};
  color: ${({ $status }) => $status === 'paid' ? '#2e7d32' : '#ed6c02'};
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $type }) => $type === 'advance' ? '#0291ef15' : '#02d3bf15'};
  color: ${({ $type }) => $type === 'advance' ? '#0291ef' : '#02d3bf'};
`;

export const useMyExpenseApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/expenses/my?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => {
    setRefreshTrigger((p) => p + 1);
  }, []);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/expenses', formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const cancel = useCallback(async (id) => {
    setFetching(true);
    try {
      await api.put(`/v1/expenses/${id}/cancel`);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const statusLabels = useMemo(() => ({
    waiting: t('expense.waiting'),
    approved: t('expense.approved'),
    rejected: t('expense.rejected'),
    canceled: t('expense.canceled'),
  }), [t]);

  const paymentLabels = useMemo(() => ({
    paid: t('expense.paid'),
    unpaid: t('expense.unpaid'),
  }), [t]);

  const typeLabels = useMemo(() => ({
    expense: t('expense.expense'),
    advance: t('expense.advance'),
  }), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'type', label: t('expense.type'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['expense', 'advance'],
          renderValue: (v) => typeLabels[v] || v,
        },
        customFilterListOptions: { render: (v) => typeLabels[v] || v },
        customBodyRender: (v) => <TypeBadge $type={v}>{typeLabels[v] || v}</TypeBadge>,
      },
    },
    { name: 'categoryName', label: t('expense.category'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'projectName', label: t('expense.project'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' }, customBodyRender: (v) => v || '—' },
    },
    {
      name: 'totalAmount', label: t('expense.totalAmount'),
      options: {
        filter: true, sort: true,
        filterType: 'textField', filterOptions: { defaultOp: 'gte' },
        customBodyRender: (v, tableMeta) => <ExpenseAmountCell row={data[tableMeta.rowIndex]} />,
      },
    },
    {
      name: 'status', label: t('expense.status'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['waiting', 'approved', 'rejected', 'canceled'],
          renderValue: (v) => statusLabels[v] || v,
        },
        customFilterListOptions: { render: (v) => statusLabels[v] || v },
        customBodyRender: (v) => <StatusBadge $status={v}>{statusLabels[v] || v}</StatusBadge>,
      },
    },
    {
      name: 'paymentStatus', label: t('expense.paymentStatus'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['paid', 'unpaid'],
          renderValue: (v) => paymentLabels[v] || v,
        },
        customFilterListOptions: { render: (v) => paymentLabels[v] || v },
        customBodyRender: (v) => <PaymentBadge $status={v}>{paymentLabels[v] || v}</PaymentBadge>,
      },
    },
    { name: 'createDate', label: t('expense.createDate'), options: { filter: false, sort: true, customBodyRender: (v) => formatDate(v, lang) } },
  ], [t, statusLabels, paymentLabels, typeLabels, data, lang]);

  return { columns, data, fetching, pagination, create, cancel, refresh };
};
